import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Calendar, User, Tag, Search } from 'lucide-react';
import { blogPosts, blogCategories, getPostById, getFeaturedPosts } from '../data/blogData';
import { SEO, pageSEO } from '../components/SEO';

// Blog List View
function BlogList() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = getFeaturedPosts();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="blog-page" className="min-h-screen pt-20">
      <SEO {...pageSEO.blog} url="/blog" />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0D4A2E] pattern-overlay">
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            CIU <span className="text-[#C5A028]">Blog</span>
          </h1>
          <p className="text-white/70 text-xl max-w-2xl">
            News, stories, and updates from CEPRES International University
          </p>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <section className="py-12 bg-[#F5F5F5]">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <h2 className="text-2xl font-bold text-[#0D4A2E] mb-6">Featured Stories</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {featuredPosts.slice(0, 2).map((post) => (
                <Link 
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="group bg-white overflow-hidden hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-[#666666] mb-3">
                      <span className="px-3 py-1 bg-[#C5A028] text-[#1A1A1A] text-xs font-semibold">
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.date)}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-[#0D4A2E] mb-2 group-hover:text-[#C5A028] transition-colors duration-300">
                      {post.title}
                    </h3>
                    <p className="text-[#666666] text-sm line-clamp-2">{post.excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-[#C5A028] font-semibold text-sm mt-4 group-hover:gap-4 transition-all duration-300">
                      <ArrowRight className="w-4 h-4" />
                      READ MORE
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Search and Filter */}
      <section className="py-8 bg-white border-b border-[#E5E5E5] sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {/* Search */}
            <div className="relative max-w-md flex-grow">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666]" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="blog-search"
                className="w-full pl-12 pr-4 py-3 border border-[#E5E5E5] bg-white focus:border-[#0D4A2E] focus:outline-none"
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {blogCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  data-testid={`filter-${category.toLowerCase()}`}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category
                      ? 'bg-[#0D4A2E] text-white'
                      : 'bg-[#F5F5F5] text-[#666666] hover:bg-[#0D4A2E] hover:text-white'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="mb-6 text-[#666666]">
            Showing {filteredPosts.length} articles
          </div>
          
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#666666] text-lg">No articles found. Try adjusting your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Link 
                  key={post.id}
                  to={`/blog/${post.id}`}
                  data-testid={`blog-post-${post.id}`}
                  className="group bg-[#F5F5F5] overflow-hidden hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-xs text-[#666666] mb-3">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.date)}
                    </div>
                    <h3 className="text-lg font-bold text-[#0D4A2E] mb-2 group-hover:text-[#C5A028] transition-colors duration-300 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-[#666666] text-sm line-clamp-3 mb-4">{post.excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-[#C5A028] font-semibold text-sm group-hover:gap-4 transition-all duration-300">
                      <ArrowRight className="w-4 h-4" />
                      READ MORE
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
          <p className="text-white/70 mb-8">
            Subscribe to receive the latest news and updates from CEPRES International University
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <input
              type="email"
              placeholder="Enter your email"
              className="px-6 py-4 bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-[#C5A028] min-w-[300px]"
            />
            <button className="px-8 py-4 bg-[#C5A028] text-[#1A1A1A] font-bold hover:bg-[#D4B43D] transition-colors duration-200">
              SUBSCRIBE
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

// Single Blog Post View
function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const post = getPostById(id);

  if (!post) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0D4A2E] mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-[#C5A028] hover:underline">Return to Blog</Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get related posts (same category, excluding current)
  const relatedPosts = blogPosts
    .filter(p => p.category === post.category && p.id !== post.id)
    .slice(0, 3);

  return (
    <div data-testid="blog-post-page" className="min-h-screen pt-20">
      <SEO 
        title={post.title}
        description={post.excerpt || post.title}
        url={`/blog/${post.id}`}
        type="article"
        image={post.image}
      />
      {/* Hero Image */}
      <section className="relative h-[400px] lg:h-[500px]">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D4A2E] via-[#0D4A2E]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16">
          <div className="max-w-4xl mx-auto">
            <button 
              onClick={() => navigate('/blog')}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </button>
            <span className="inline-block px-4 py-1 bg-[#C5A028] text-[#1A1A1A] text-sm font-semibold mb-4">
              {post.category}
            </span>
            <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-white/70 text-sm">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(post.date)}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-[#666666] mb-8 leading-relaxed">{post.excerpt}</p>
            {post.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-[#1A1A1A] leading-relaxed mb-6">
                {paragraph.trim()}
              </p>
            ))}
          </div>

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-[#E5E5E5]">
            <div className="flex items-center gap-4">
              <span className="text-[#666666] font-medium">Share this article:</span>
              <div className="flex gap-2">
                {['Facebook', 'Twitter', 'LinkedIn'].map((platform) => (
                  <button 
                    key={platform}
                    className="px-4 py-2 bg-[#F5F5F5] text-[#666666] text-sm hover:bg-[#0D4A2E] hover:text-white transition-colors duration-200"
                  >
                    {platform}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-[#F5F5F5]">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <h2 className="text-2xl font-bold text-[#0D4A2E] mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link 
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.id}`}
                  className="group bg-white overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img 
                      src={relatedPost.image} 
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-[#0D4A2E] group-hover:text-[#C5A028] transition-colors duration-300 line-clamp-2">
                      {relatedPost.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// Main Export - Router will handle which component to show
export default function Blog() {
  const { id } = useParams();
  return id ? <BlogPost /> : <BlogList />;
}

export { BlogList, BlogPost };
